import { useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Loader2, Search, Calculator, Hash, Activity, Ruler, Network, ChevronDown, ChevronRight, List } from 'lucide-react';
import { usePipelineNodes } from '../hooks/usePipelineNodes';
import { useMeasures } from '../hooks/useMeasures';
import { useDimensionsList } from '../hooks/useDimension';
import { Input } from './ui/input';
import { getPipelineNodeIcon } from './PipelineNodeIcon';
import { SourceIcon } from './SourceIcon';
import { EditableRegion } from '../contexts/ReadOnlyContext';
import { SidebarTrigger } from './ui/sidebar';
import { QuickNavDetailPanel, type QuickNavSelection } from './QuickNavDetailPanel';
import {
  ENTITY_NODE_TYPES,
  isSourceNodeType,
  isTransformationNodeType,
  sourceIconId,
} from '../services/pipelineNode.service';
import type { PipelineNode } from '../services/pipelineNode.service';
import type { Measure, MetricType } from '../services/measure.service';
import type { Dimension } from '../services/dimension.service';

export const DATA_MODEL_BASE_PATH = '/data-model';
export const MODELS_BASE_PATH = `${DATA_MODEL_BASE_PATH}/models`;
/** Sources and transformations share MODELS_BASE_PATH for detail, but list separately. */
export const SOURCES_LIST_PATH = `${DATA_MODEL_BASE_PATH}/sources`;
export const TRANSFORMATIONS_LIST_PATH = `${DATA_MODEL_BASE_PATH}/transformations`;


function getMetricTypeIcon(metricType: MetricType) {
  switch (metricType) {
    case 'CALCULATED':
      return <Calculator className="h-3.5 w-3.5 text-muted-foreground" />;
    case 'COHORT':
      return <Activity className="h-3.5 w-3.5 text-muted-foreground" />;
    case 'BASIC':
    default:
      return <Hash className="h-3.5 w-3.5 text-muted-foreground" />;
  }
}

interface QuickNavItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  /** Where a double click goes. Single click only selects. */
  detailPath: string;
}

/** The object the detail panel is pointed at, independent of the route. */
type SelectedRef = { kind: 'node' | 'metric' | 'dimension'; id: string };

type SectionKey = 'sources' | 'transformations' | 'entities' | 'metrics' | 'dimensions';

function byLabel(a: QuickNavItem, b: QuickNavItem) {
  return a.label.localeCompare(b.label, undefined, { sensitivity: 'base' });
}

/**
 * Quick Nav shared by every page under SemanticLayerLayout, ordered the way data moves:
 * the sources it arrives from, the transformations applied to them, then the entities,
 * metrics and dimensions built on top. Rows select rather than
 * navigate — a single click points the detail panel below at an object and leaves the route
 * alone; double clicking a row, or the panel's "Go to Object" button, is what navigates.
 * Landing on a detail page by any other route selects that object, so the panel still follows
 * the URL when the URL moves. Section headings expand and collapse; the icon beside their
 * count opens the full list page.
 */
export function SemanticLayerQuickNav() {
  const [searchQuery, setSearchQuery] = useState('');
  const [picked, setPicked] = useState<{ route: string; ref: SelectedRef } | null>(null);
  const [sectionOverrides, setSectionOverrides] = useState<Partial<Record<SectionKey, boolean>>>({});
  const { pathname } = useLocation();
  const navigate = useNavigate();

  // Same query params the semantic layer pages already use, so this shares their cache
  // rather than issuing a parallel request.
  const { data: nodesData, isLoading: isLoadingNodes } = usePipelineNodes({
    page: 1,
    pageSize: 1000,
  });
  const { data: measuresData, isLoading: isLoadingMeasures } = useMeasures({
    page: 1,
    pageSize: 1000,
    sort: 'name',
  });
  const { data: dimensionsData, isLoading: isLoadingDimensions } = useDimensionsList({
    pageSize: 1000,
  });

  const isLoading = isLoadingNodes || isLoadingMeasures || isLoadingDimensions;

  const { sources, transformations, entities, metrics, dimensions } = useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    const matches = (label: string) => !query || label.toLowerCase().includes(query);

    const toNodeItem = (fallbackLabel: string) => (n: PipelineNode) => ({
      id: n.id,
      label: n.label || n.name || fallbackLabel,
      icon: getPipelineNodeIcon(n.node_type, 'h-3.5 w-3.5 text-muted-foreground'),
      detailPath: `${MODELS_BASE_PATH}/${n.id}`,
    });

    // A source shows its connector's logo, the same as on the diagram — the generic
    // database icon made every row look alike. Falls back to that icon when the node
    // has no connector to point at.
    const sourceItems: QuickNavItem[] = (nodesData?.records || [])
      .filter((n: PipelineNode) => isSourceNodeType(n.node_type))
      .map((n: PipelineNode) => {
        const iconSourceId = sourceIconId(n.node_type, n.source_id, n.flat_file);
        return {
          ...toNodeItem('Unnamed Source')(n),
          icon: iconSourceId ? (
            <SourceIcon sourceId={iconSourceId} size={14} className="shrink-0" />
          ) : (
            getPipelineNodeIcon(n.node_type, 'h-3.5 w-3.5 text-muted-foreground')
          ),
        };
      })
      .filter((item) => matches(item.label))
      .sort(byLabel);

    const transformationItems: QuickNavItem[] = (nodesData?.records || [])
      .filter((n: PipelineNode) => isTransformationNodeType(n.node_type))
      .map(toNodeItem('Unnamed Transformation'))
      .filter((item) => matches(item.label))
      .sort(byLabel);

    const entityItems: QuickNavItem[] = (nodesData?.records || [])
      .filter((n: PipelineNode) => ENTITY_NODE_TYPES.includes(n.node_type))
      .map((n: PipelineNode) => ({
        id: n.id,
        label: n.label || n.name || 'Unnamed Entity',
        icon: getPipelineNodeIcon(n.node_type, 'h-3.5 w-3.5 text-muted-foreground'),
        detailPath: `${DATA_MODEL_BASE_PATH}/entities/${n.id}`,
      }))
      .filter((item) => matches(item.label))
      .sort(byLabel);

    const metricItems: QuickNavItem[] = (measuresData?.records || [])
      .map((m: Measure) => ({
        id: m.id,
        label: m.name || 'Unnamed Metric',
        icon: getMetricTypeIcon(m.metric_type),
        detailPath: `${DATA_MODEL_BASE_PATH}/metrics/${m.id}`,
      }))
      .filter((item) => matches(item.label))
      .sort(byLabel);

    const dimensionItems: QuickNavItem[] = (dimensionsData?.records || [])
      .map((d: Dimension) => ({
        id: d.id,
        label: d.name || 'Unnamed Dimension',
        icon: <Ruler className="h-3.5 w-3.5 text-muted-foreground" />,
        detailPath: `${DATA_MODEL_BASE_PATH}/dimensions/${d.id}`,
      }))
      .filter((item) => matches(item.label))
      .sort(byLabel);

    return {
      sources: sourceItems,
      transformations: transformationItems,
      entities: entityItems,
      metrics: metricItems,
      dimensions: dimensionItems,
    };
  }, [nodesData?.records, measuresData?.records, dimensionsData?.records, searchQuery]);

  // Which section the object in the URL lives in. Read from the path where the path
  // says enough, which is everywhere except a node: sources and transformations share
  // one detail route, so which of the two holds it is a question only the node's type
  // answers. That means no section expands on the very first render of a node page,
  // until the records land — better than expanding the wrong one.
  const routeSection: SectionKey | null = useMemo(() => {
    if (pathname.startsWith(`${MODELS_BASE_PATH}/`)) {
      const id = pathname.slice(`${MODELS_BASE_PATH}/`.length);
      const node = (nodesData?.records || []).find((n: PipelineNode) => n.id === id);
      if (!node) return null;
      return isSourceNodeType(node.node_type) ? 'sources' : 'transformations';
    }
    if (pathname.startsWith(`${DATA_MODEL_BASE_PATH}/entities/`)) return 'entities';
    if (pathname.startsWith(`${DATA_MODEL_BASE_PATH}/metrics/`)) return 'metrics';
    if (pathname.startsWith(`${DATA_MODEL_BASE_PATH}/dimensions/`)) return 'dimensions';
    return null;
  }, [pathname, nodesData?.records]);

  // Everything starts collapsed except the section holding the object you're on — and, while
  // searching, any section with matches, since a hidden match is a match you can't find.
  // Toggling a section overrides both, and the override sticks as you move around.
  const isSectionExpanded = (section: SectionKey) =>
    sectionOverrides[section] ?? (section === routeSection || !!searchQuery.trim());

  // select-none so a double click navigates instead of highlighting the label.
  const rowClass = (isActive: boolean) =>
    `w-full text-left px-2 py-1 rounded-md text-xs flex items-center gap-2 transition-colors select-none ${
      isActive ? 'bg-accent text-accent-foreground' : 'hover:bg-accent/50 text-foreground'
    }`;

  const renderSelectableItem = (kind: SelectedRef['kind']) => (item: QuickNavItem) => {
    const isActive = selectedRef?.kind === kind && selectedRef.id === item.id;
    return (
      <button
        key={item.id}
        type="button"
        onClick={() => setPicked({ route: pathname, ref: { kind, id: item.id } })}
        onDoubleClick={() => navigate(item.detailPath)}
        className={rowClass(isActive)}
      >
        {item.icon}
        <span className="truncate">{item.label}</span>
      </button>
    );
  };

  const renderSection = (
    section: SectionKey,
    title: string,
    listPath: string,
    kind: SelectedRef['kind'],
    items: QuickNavItem[]
  ) => {
    if (items.length === 0) return null;
    const expanded = isSectionExpanded(section);
    const Chevron = expanded ? ChevronDown : ChevronRight;
    return (
      <div>
        <div
          className={`w-full flex items-center gap-1 mb-2 text-[11px] font-medium uppercase tracking-wide ${
            pathname === listPath ? 'text-foreground' : 'text-muted-foreground'
          }`}
        >
          <button
            type="button"
            onClick={() => setSectionOverrides((prev) => ({ ...prev, [section]: !expanded }))}
            className="flex-1 min-w-0 flex items-center gap-1 select-none transition-colors hover:text-foreground"
            aria-expanded={expanded}
          >
            <Chevron className="h-3 w-3 shrink-0" />
            <span className="truncate">{title}</span>
          </button>
          <span className="tabular-nums normal-case">{items.length}</span>
          <Link
            to={listPath}
            title={`View all ${title.toLowerCase()}`}
            aria-label={`View all ${title.toLowerCase()}`}
            className="shrink-0 p-0.5 -mr-0.5 rounded transition-colors hover:bg-accent/50 hover:text-foreground"
          >
            <List className="h-3 w-3" />
          </Link>
        </div>
        {expanded && <div className="space-y-1">{items.map(renderSelectableItem(kind))}</div>}
      </div>
    );
  };

  const isEmpty =
    sources.length === 0 &&
    transformations.length === 0 &&
    entities.length === 0 &&
    metrics.length === 0 &&
    dimensions.length === 0;

  // The object the route points at, if any — used to seed the selection so arriving at a
  // detail page (deep link, "Go to Object", a link from a page) shows it in the panel.
  const routeRef: SelectedRef | null = useMemo(() => {
    const nodeMatch = pathname.match(
      new RegExp(`^(?:${MODELS_BASE_PATH}|${DATA_MODEL_BASE_PATH}/entities)/([^/]+)$`)
    );
    if (nodeMatch) return { kind: 'node', id: nodeMatch[1] };

    const metricMatch = pathname.match(new RegExp(`^${DATA_MODEL_BASE_PATH}/metrics/([^/]+)$`));
    if (metricMatch) return { kind: 'metric', id: metricMatch[1] };

    const dimensionMatch = pathname.match(new RegExp(`^${DATA_MODEL_BASE_PATH}/dimensions/([^/]+)$`));
    if (dimensionMatch) return { kind: 'dimension', id: dimensionMatch[1] };

    return null;
  }, [pathname]);

  // A row click selects without navigating, so the pick is scoped to the route it was made
  // on: once the URL moves the route's own object takes over again (and a route with no
  // object — a list page — starts empty rather than showing a stale pick).
  const selectedRef = picked?.route === pathname ? picked.ref : routeRef;

  const selection: QuickNavSelection | null = useMemo(() => {
    if (!selectedRef) return null;

    if (selectedRef.kind === 'node') {
      const node = (nodesData?.records || []).find((n: PipelineNode) => n.id === selectedRef.id);
      if (!node) return null;
      const detailPath = ENTITY_NODE_TYPES.includes(node.node_type)
        ? `${DATA_MODEL_BASE_PATH}/entities/${node.id}`
        : `${MODELS_BASE_PATH}/${node.id}`;
      return { kind: 'node', node, detailPath };
    }

    if (selectedRef.kind === 'metric') {
      const measure = (measuresData?.records || []).find((m: Measure) => m.id === selectedRef.id);
      return {
        kind: 'metric',
        label: measure?.name || 'Metric',
        detailPath: `${DATA_MODEL_BASE_PATH}/metrics/${selectedRef.id}`,
      };
    }

    const dimension = (dimensionsData?.records || []).find((d: Dimension) => d.id === selectedRef.id);
    return {
      kind: 'dimension',
      label: dimension?.name || 'Dimension',
      detailPath: `${DATA_MODEL_BASE_PATH}/dimensions/${selectedRef.id}`,
    };
  }, [selectedRef, nodesData?.records, measuresData?.records, dimensionsData?.records]);

  return (
    <div className="w-80 border-r border-border flex flex-col flex-shrink-0 h-full overflow-hidden">
      <div className="flex-1 min-h-0 overflow-auto p-4">
        <div className="flex items-center gap-4 mb-3">
          {/* These pages have no top bar, so this is the only way back to the app nav on mobile. */}
          <SidebarTrigger className="md:hidden h-5 w-5 shrink-0" />
          <h2 className="text-xs font-semibold text-foreground shrink-0">Quick Nav</h2>
          {/* Quick-nav filter — navigation, not an edit. */}
          <EditableRegion>
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-7 pl-6 text-xs"
              />
            </div>
          </EditableRegion>
        </div>
        {/* A fixed destination rather than a selectable object, so it navigates on click and
            the search never hides it. */}
        <div className="mb-4">
          <Link
            to={`${DATA_MODEL_BASE_PATH}/diagram`}
            className={rowClass(pathname === `${DATA_MODEL_BASE_PATH}/diagram`)}
          >
            <Network className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="truncate">Diagram</span>
          </Link>
        </div>
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-4 w-4 animate-spin text-primary" />
          </div>
        ) : isEmpty ? (
          <p className="text-xs text-muted-foreground py-4">
            {searchQuery.trim() ? 'No matches.' : 'Nothing to show yet.'}
          </p>
        ) : (
          <div className="space-y-4">
            {renderSection('sources', 'Sources', SOURCES_LIST_PATH, 'node', sources)}
            {renderSection(
              'transformations',
              'Transformations',
              TRANSFORMATIONS_LIST_PATH,
              'node',
              transformations
            )}
            {renderSection('entities', 'Entities', `${DATA_MODEL_BASE_PATH}/entities`, 'node', entities)}
            {renderSection('metrics', 'Metrics', `${DATA_MODEL_BASE_PATH}/metrics`, 'metric', metrics)}
            {renderSection('dimensions', 'Dimensions', `${DATA_MODEL_BASE_PATH}/dimensions`, 'dimension', dimensions)}
          </div>
        )}
      </div>

      <QuickNavDetailPanel selection={selection} />
    </div>
  );
}
